import { useLingui } from '@lingui/react'
import { t, Trans } from '@lingui/macro'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { LoadingButton } from '@mui/lab'
import { Grid } from '@mui/material'
import DateRangePickerInput from '@/components/ui/inputs/DateRangePickerInput'
import TextInputController from '@/components/ui/inputs/TextInputController'

const schema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  dateRange: z.object({
    start: z.date(),
    end: z.date(),
  }),
})

export type PlanningFormData = z.infer<typeof schema>

type PlanningFormProps = Readonly<{
  defaultValues?: PlanningFormData
  formId?: string
  isEditMode?: boolean
  isSubmitting?: boolean
  onSubmit: (data: PlanningFormData) => void
}>

export function PlanningForm({ formId = 'planning-form', onSubmit, defaultValues, isEditMode = false, isSubmitting = false }: PlanningFormProps) {
  useLingui()

  const { control, handleSubmit } = useForm<PlanningFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues ?? {
      name: '',
      description: '',
      dateRange: {
        start: new Date(),
        end: new Date(),
      },
    },
  })

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextInputController control={control} label={t`Name`} fullWidth name="name" />
        </Grid>
        <Grid item xs={12}>
          <TextInputController control={control} label={t`Description`} fullWidth multiline name="description" rows={3} />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="dateRange"
            control={control}
            render={({ field: { value, onChange } }) => {
              return <DateRangePickerInput value={[value.start, value.end]} onChange={onChange} />
            }}
          />
        </Grid>
        {!isEditMode && (
          <Grid item xs={12}>
            <LoadingButton loading={isSubmitting} type="submit" variant="contained" color="primary" fullWidth>
              <Trans>Submit</Trans>
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    </form>
  )
}
